import { useState } from "react";
import { motion } from "framer-motion";
import { DIMENSIONS } from "../../data/framework";

const C = 240;
const R = 158;
const pts = DIMENSIONS.map((_, i) => {
  const a = ((-90 + i * 72) * Math.PI) / 180;
  return { x: C + R * Math.cos(a), y: C + R * Math.sin(a) };
});

export const DimensionsGraphic = ({ dimensions = DIMENSIONS, centerLabel = ["ENTERPRISE", "AI Value"] }) => {
  const [active, setActive] = useState(null);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-testid="dimensions-graphic">
      <div className="max-w-md mx-auto w-full">
        <svg viewBox="0 0 480 480" className="w-full h-auto" role="img" aria-label="Five interconnected dimensions of AI success">
          {pts.map((p, i) =>
            pts.slice(i + 1).map((q, j) => (
              <motion.line
                key={`${i}-${j}`}
                x1={p.x} y1={p.y} x2={q.x} y2={q.y}
                stroke="#C4A277"
                strokeWidth="1"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 0.45 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: 0.2 + (i + j) * 0.08 }}
              />
            ))
          )}
          {pts.map((p, i) => (
            <motion.line
              key={`c-${i}`}
              x1={C} y1={C} x2={p.x} y2={p.y}
              stroke="#2C3E35"
              strokeWidth="1.2"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.4 + i * 0.1 }}
              opacity={active === null || active === i ? 0.6 : 0.15}
              style={{ transition: "opacity .3s" }}
            />
          ))}
          <motion.g
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", delay: 0.2 }}
            style={{ transformOrigin: `${C}px ${C}px` }}
          >
            <circle cx={C} cy={C} r="58" fill="#2C3E35" />
            <text x={C} y={C - 4} textAnchor="middle" fontSize="13" fill="#E3CBA8" fontFamily="Manrope, sans-serif" letterSpacing="2" fontWeight="600">{centerLabel[0]}</text>
            <text x={C} y={C + 16} textAnchor="middle" fontSize="15" fill="#FDFCF8" fontFamily="Cormorant Garamond, serif" fontWeight="600">{centerLabel[1]}</text>
          </motion.g>
          {pts.map((p, i) => {
            const isActive = active === i;
            return (
              <motion.g
                key={i}
                className="cursor-pointer"
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                onClick={() => setActive(isActive ? null : i)}
                data-testid={`dimension-node-${dimensions[i].name.toLowerCase()}`}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring", delay: 0.5 + i * 0.12 }}
                style={{ transformOrigin: `${p.x}px ${p.y}px` }}
              >
                <circle cx={p.x} cy={p.y} r="46" fill={isActive ? "#C4A277" : "#FDFCF8"} stroke={isActive ? "#C4A277" : "#2C3E35"} strokeWidth="1.5" style={{ transition: "fill .25s, stroke .25s" }} />
                <text x={p.x} y={p.y + 5} textAnchor="middle" fontSize="15" fontWeight="500" fill={isActive ? "#FDFCF8" : "#1C1C1C"} fontFamily="Manrope, sans-serif" style={{ transition: "fill .25s" }}>
                  {dimensions[i].name}
                </text>
              </motion.g>
            );
          })}
        </svg>
      </div>
      <ul className="space-y-0 divide-y divide-summit-border">
        {dimensions.map((d, i) => (
          <li
            key={d.name}
            data-testid={`dimension-item-${d.name.toLowerCase()}`}
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(null)}
            className={`py-5 grid grid-cols-[120px_1fr] gap-4 items-baseline transition-colors ${active === i ? "text-summit-gold-dark" : ""}`}
          >
            <span className="font-heading text-2xl font-medium text-summit-charcoal">{d.name}</span>
            <span className="font-body text-sm md:text-base text-summit-text-muted leading-relaxed">{d.description}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
